import * as React from 'react'
import * as Sentry from '@sentry/react'
import { useReducedMotion } from 'framer-motion'
import * as Dialog from 'primitives/dialog'
import { Z_INDEX_MODALS } from 'constants/modals'
import { isProduction } from 'utils/env'
import { STUDIO_AI_EXCLUSIVE_OFFER_MODAL_DATA } from 'constants/local-storage'
import { SentryFilterTagPaymentsDialog } from '@repo/sentry-utils/constants'
import { useUserIsEligibleForStudioAiYearlyUpgradeQuery } from '@repo/cf-user/hooks/use-user-data'
import { useFeatureValue } from 'growthbook'
import { STUDIO_AI_EXCLUSIVE_OFFER_MODAL_START_COUNTER_EVENT_NAME } from '../payments-dialog/components/payments-complete-screen-dialog-types'
import { usePaymentsAppType } from './payments-app-type'
import { sendAmplitudeEvent, AmplitudeEvents } from '@repo/analytics/amplitude/utils'
import { lazyComponent } from 'utils/lazy-component'
import { localStore } from 'utils/storage'

import { Confetti } from './confetti'

const Message = lazyComponent(() =>
  import('./studio-ai-exclusive-offer-screen-dialog-message').then(m => m.Message),
)

interface StudioAiExclusiveOfferScreenDialogProps {
  postCloseBehaviour?: 'redirectToStudio' | 'none'
}

export function StudioAiExclusiveOfferScreenDialog({
  postCloseBehaviour = 'redirectToStudio',
}: StudioAiExclusiveOfferScreenDialogProps) {
  const prefersReducedMotion = useReducedMotion()
  const [isOpen, setIsOpen] = React.useState(false)
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null)
  const { data: isEligibleForStudioAiYearlyUpgrade } =
    useUserIsEligibleForStudioAiYearlyUpgradeQuery()
  const isStudioAiExclusiveOfferEnabled = useFeatureValue(
    'GROW-1930-enable-studio-exclusive-offer',
    -1,
  )
  const appType = usePaymentsAppType()

  const handleClose = React.useCallback(() => {
    setIsOpen(false)
    if (postCloseBehaviour === 'redirectToStudio' && appType !== 'studio') {
      window.location.href = isProduction
        ? 'https://studio.creativefabrica.com'
        : 'https://studio.staging.creativefabrica.com'
    }
    localStore.remove(STUDIO_AI_EXCLUSIVE_OFFER_MODAL_DATA)
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [postCloseBehaviour, appType])

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose()
      }
    },
    [handleClose],
  )

  React.useEffect(() => {
    if (isOpen) {
      sendAmplitudeEvent(AmplitudeEvents.STUDIO_AI_EXCLUSIVE_OFFER_SCREEN_VIEWED)
    }
  }, [isOpen])

  React.useEffect(() => {
    const handleModalOpen = () => {
      try {
        //Check local storage to see if we have already shown the modal
        const localStorageValue = localStore.getString(STUDIO_AI_EXCLUSIVE_OFFER_MODAL_DATA)
        if (
          !localStorageValue ||
          !isEligibleForStudioAiYearlyUpgrade ||
          isStudioAiExclusiveOfferEnabled === -1
        ) {
          return
        }
        //If we have shown it, do not show it again
        if (!intervalRef.current) {
          intervalRef.current = setInterval(() => {
            const now = new Date()
            const diffInSeconds = Math.abs(now.getTime() - Number(localStorageValue)) / 1000
            //If less than 90 seconds have passed, do not show the modal
            if (diffInSeconds < isStudioAiExclusiveOfferEnabled) {
              setIsOpen(false)
            } else {
              setIsOpen(true)
              //After showing the modal, remove the local storage item
              localStore.remove(STUDIO_AI_EXCLUSIVE_OFFER_MODAL_DATA)
              //Clear the interval
              if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
              }
            }
          }, 1000)
        }
        // oxlint-disable-next-line unicorn/catch-error-name
      } catch (e) {
        Sentry.captureException(
          new Error(
            `Local storage not available: ${(e as Error)?.message ? (e as Error)?.message : (e as string)}`,
          ),
          {
            tags: {
              component: SentryFilterTagPaymentsDialog,
            },
          },
        )
      }
    }

    handleModalOpen()
    document.addEventListener(
      STUDIO_AI_EXCLUSIVE_OFFER_MODAL_START_COUNTER_EVENT_NAME,
      handleModalOpen,
    )

    return () => {
      document.removeEventListener(
        STUDIO_AI_EXCLUSIVE_OFFER_MODAL_START_COUNTER_EVENT_NAME,
        handleModalOpen,
      )
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isEligibleForStudioAiYearlyUpgrade, isStudioAiExclusiveOfferEnabled])

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} defaultOpen={false}>
      <Dialog.Portal forceMount>
        <div data-cf-external>
          <Dialog.Body overlayZIndex={Z_INDEX_MODALS.default - 1} zIndex={Z_INDEX_MODALS.default}>
            <div className="relative h-full max-h-[623px] w-full max-w-[1007px] p-[16px] text-gray-900">
              <div className="relative flex h-full w-full flex-col items-center justify-center rounded-[16px] bg-[#fff] p-[16px]">
                {!prefersReducedMotion ? <Confetti /> : null}
                {isOpen ? <Message.component onClose={handleClose} /> : null}
              </div>
            </div>
          </Dialog.Body>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
